import React from 'react'
import { formatInTimeZone } from 'date-fns-tz'
import { Slot } from '@allara-health/source-health-client'
import { Chip } from 'components/Scheduling/Calendar'
import { EnhancedProvider } from 'containers/Scheduling/types'
import { CSS_CLASSES, TIME_FORMAT } from './constants'
import { SlotPhaseData, ChipState } from './types'

interface SlotRendererProps {
  slotPhaseData: SlotPhaseData
  provider: EnhancedProvider
  timeZone?: string
  chipState: (state: ChipState) => string
  onSlotClick: (slot: Slot, provider: EnhancedProvider) => void
  onExpandClick: (providerId: string, nextPhase: number) => void
}

/**
 * Renders a single slot chip
 */
const SlotChip: React.FC<{
  slot: Slot
  provider: EnhancedProvider
  timeZone?: string
  chipState: (state: ChipState) => string
  onSlotClick: (slot: Slot, provider: EnhancedProvider) => void
}> = ({ slot, provider, timeZone, chipState, onSlotClick }) => (
  <Chip
    onClick={() => onSlotClick(slot, provider)}
    key={slot.start_at}
    text={formatInTimeZone(
      new Date(slot.start_at),
      timeZone ?? '',
      TIME_FORMAT
    )}
    state={`${chipState('active')} chip-slot w-100 time-slot-chip`}
  />
)

/**
 * Renders a row of slots
 */
const SlotRow: React.FC<{
  slots: Slot[]
  rowIndex: number
  slotsByRow: number
  provider: EnhancedProvider
  timeZone?: string
  chipState: (state: ChipState) => string
  onSlotClick: (slot: Slot, provider: EnhancedProvider) => void
}> = ({
  slots,
  rowIndex,
  slotsByRow,
  provider,
  timeZone,
  chipState,
  onSlotClick,
}) => (
  <div key={`row-${rowIndex}`} className={CSS_CLASSES.SLOT_ROW}>
    {Array.from({ length: slotsByRow }).map((_, colIndex) => {
      const slotIndex = rowIndex * slotsByRow + colIndex
      const slot = slotIndex < slots.length ? slots[slotIndex] : undefined

      return (
        <div
          key={`slot-${rowIndex}-${colIndex}`}
          className={CSS_CLASSES.SLOT_CELL}
          style={{ flex: CSS_CLASSES.FLEX_STYLE }}
        >
          {slot && (
            <SlotChip
              slot={slot}
              provider={provider}
              timeZone={timeZone}
              chipState={chipState}
              onSlotClick={onSlotClick}
            />
          )}
        </div>
      )
    })}
  </div>
)

/**
 * Renders the expand/show less buttons
 */
const ExpandButtons: React.FC<{
  hasMoreSlots: boolean
  canGoBack: boolean
  currentPhase: number
  providerId: string
  onExpandClick: (providerId: string, nextPhase: number) => void
  getButtonText: (phase: number) => string
}> = ({
  hasMoreSlots,
  canGoBack,
  currentPhase,
  providerId,
  onExpandClick,
  getButtonText,
}) => (
  <>
    {hasMoreSlots && (
      <div className={CSS_CLASSES.EXPAND_BUTTON_CONTAINER}>
        <div
          className={CSS_CLASSES.EXPAND_BUTTON}
          onClick={() => onExpandClick(providerId, currentPhase + 1)}
        >
          {getButtonText(currentPhase)}
        </div>
      </div>
    )}

    {canGoBack && (
      <div className={CSS_CLASSES.SHOW_LESS_BUTTON_CONTAINER}>
        <div
          className={CSS_CLASSES.EXPAND_BUTTON}
          onClick={() => onExpandClick(providerId, currentPhase - 1)}
        >
          Show less
        </div>
      </div>
    )}
  </>
)

/**
 * Main slot renderer component
 */
export const SlotRenderer: React.FC<SlotRendererProps> = ({
  slotPhaseData,
  provider,
  timeZone,
  chipState,
  onSlotClick,
  onExpandClick,
}) => {
  const {
    sortedSlots,
    visibleRows,
    slotsByRow,
    hasMoreSlots,
    canGoBack,
    currentPhase,
    getButtonText,
  } = slotPhaseData

  // If no slots available, don't render anything
  if (sortedSlots.length === 0) {
    return null
  }

  return (
    <div className={CSS_CLASSES.PROVIDER_SLOTS_CONTAINER}>
      {Array.from({ length: visibleRows }).map((_, rowIndex) => (
        <SlotRow
          key={rowIndex}
          slots={sortedSlots}
          rowIndex={rowIndex}
          slotsByRow={slotsByRow}
          provider={provider}
          timeZone={timeZone}
          chipState={chipState}
          onSlotClick={onSlotClick}
        />
      ))}

      <ExpandButtons
        hasMoreSlots={hasMoreSlots}
        canGoBack={canGoBack}
        currentPhase={currentPhase}
        providerId={provider.id}
        onExpandClick={onExpandClick}
        getButtonText={getButtonText}
      />
    </div>
  )
}
